// SPDX-License-Identifier: GPL-3.0-or-later
// Playfield art of the SigmaBots table, drawn on a canvas (THEME.md sections 2 and 4).
// All text is #FFFFFF. #FF0000 is used for fills only (arrows). Grays are neutral lines.
// The code draws in table units (1000 x 2000). It scales the canvas to the table.

import { BRAND, type Ctx2DLike, monoFont } from "./canvas.ts";
import { splitLabel } from "./labels.ts";

/** Table size in units. */
export const PLAYFIELD_WIDTH = 1000;
export const PLAYFIELD_HEIGHT = 2000;

/** Side of the square behind a lane letter, in table units. */
export const LANE_BOX = 66;

/**
 * Side of the M lane square. The M lane is narrow: the inlane guide face is at x 865 and the
 * shooter wall face is at x 922, a gap of 57 units. A 48 unit square with a 4 unit stroke leaves
 * 2.5 units on each side.
 */
export const LANE_BOX_M = 48;

/** The four lane squares: the letter, the center x and the side. The center y is 1650. */
export const LANE_SQUARES: readonly {
  readonly letter: string;
  readonly x: number;
  readonly box: number;
}[] = [
  { letter: "L", x: 95, box: LANE_BOX },
  { letter: "G", x: 185, box: LANE_BOX },
  { letter: "T", x: 815, box: LANE_BOX },
  { letter: "M", x: 893.5, box: LANE_BOX_M },
];

/**
 * One text label on the playfield. Positions are table units. Rotation is in degrees.
 * The camera sees the table from far away, so the sizes are large: a phone shows 1 unit as
 * about 0.3 px. Each label is placed so that it does not run under a lamp, a target or a wall.
 */
export interface PlayfieldLabel {
  readonly text: string;
  readonly x: number;
  readonly y: number;
  /** Font size in table units. */
  readonly size: number;
  readonly rotation?: number;
  readonly alpha?: number;
  /** Squeeze the text to this width (table units) when it is wider. */
  readonly maxWidth?: number;
  /** Break the text into two lines at the space nearest the middle. */
  readonly wrap?: boolean;
}

/** The lines that a label draws. */
export function labelLines(label: PlayfieldLabel): string[] {
  return label.wrap ? splitLabel(label.text, 1) : [label.text];
}

/** Every text label that the art draws. Shot names follow THEME.md section 4.2 (the SigmaBot names). */
export const PLAYFIELD_LABELS: readonly PlayfieldLabel[] = [
  { text: "SIGMABOTS", x: 500, y: 1240, size: 64, alpha: 0.35 },
  // The four lanes: the name ends above the lane letter lamp (y 1518).
  { text: "GUARD LANE", x: 85, y: 1437, size: 22, rotation: -90 },
  { text: "ROLLBACK", x: 178, y: 1437, size: 22, rotation: -90 },
  { text: "REVERT", x: 822, y: 1437, size: 22, rotation: -90 },
  { text: "FORCE PUSH", x: 892, y: 1437, size: 22, rotation: -90 },
  // The slingshot names stand beside the slingshot face, parallel to it (60 degrees), about 30
  // units off the face. They touch no sling wall and no inlane guide.
  { text: "RETRY", x: 301, y: 1590, size: 26, rotation: 60 },
  { text: "UNDO", x: 699, y: 1590, size: 26, rotation: -60 },
  // The lane letters: large, inside the 66 unit squares.
  { text: "L", x: 95, y: 1650, size: 52 },
  { text: "G", x: 185, y: 1650, size: 52 },
  { text: "T", x: 815, y: 1650, size: 52 },
  // The M square is smaller and stands in the middle of the gap between the inlane guide (x 865)
  // and the shooter wall (x 922). It touches neither.
  { text: "M", x: 893.5, y: 1650, size: 40 },
  { text: "BOT ORBIT", x: 72, y: 780, size: 24, rotation: -90 },
  { text: "10X ORBIT", x: 886, y: 780, size: 24, rotation: -90 },
  // The wire ramps arch over the middle of the table. A label stands where no wire, wall,
  // lamp or character crosses it, seen from the camera at 720 x 900 and at 390 x 844.
  // Below the saucer lamps, right of the left ramp leg.
  { text: "GHOST RAMP", x: 340, y: 985, size: 22, maxWidth: 104 },
  // Left of the right ramp leg, under the saucer.
  { text: "MERGE RAMP", x: 590, y: 975, size: 18, wrap: true },
  // Above the pop bumpers. The ramp arch is below them.
  { text: "TOKEN BURN", x: 462, y: 322, size: 26 },
  // Under the spinner arrow, left of the right ramp leg.
  { text: "BOT SPINNER", x: 588, y: 900, size: 18, wrap: true },
  { text: "PROD BOT SAUCER", x: 420, y: 1060, size: 22 },
  // Under the left orbit rail, left of the left ramp leg, away from the orbit lamp (75, 1030).
  { text: "CONTEXT WINDOW", x: 125, y: 1140, size: 18, wrap: true },
  { text: "TOKENS", x: 125, y: 1102, size: 20 },
  // Left of the right ramp leg.
  { text: "DENY", x: 695, y: 1085, size: 40 },
  // Wrapped, so that it ends before the right orbit rail (x 842).
  { text: "BOT REVIEW", x: 765, y: 792, size: 22, wrap: true },
  { text: "PLUNGER", x: 956, y: 1820, size: 18, rotation: -90 },
];

/** Red filled arrows (fills only). Each has a center, a direction in degrees (0 = up) and a size. */
export const PLAYFIELD_ARROWS: readonly {
  readonly x: number;
  readonly y: number;
  readonly angle: number;
  readonly size: number;
}[] = [
  // Under the two ORBIT lamps (y 1030, 22 high), so no arrow runs under a lamp.
  { x: 75, y: 1090, angle: 0, size: 22 },
  { x: 885, y: 1090, angle: 0, size: 22 },
  { x: 335, y: 855, angle: 0, size: 22 },
  { x: 665, y: 855, angle: 0, size: 22 },
  { x: 520, y: 840, angle: 0, size: 16 },
  // Saucer kick-out: toward the right flipper (167 degrees: down and a little right).
  { x: 462, y: 1000, angle: 167, size: 22 },
];

function drawArrow(ctx: Ctx2DLike, x: number, y: number, angleDeg: number, size: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((angleDeg * Math.PI) / 180);
  ctx.fillStyle = BRAND.mark;
  ctx.beginPath();
  ctx.moveTo(0, -size);
  ctx.lineTo(size * 0.7, size * 0.6);
  ctx.lineTo(-size * 0.7, size * 0.6);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawLabel(ctx: Ctx2DLike, label: PlayfieldLabel): void {
  ctx.save();
  ctx.translate(label.x, label.y);
  if (label.rotation) ctx.rotate((label.rotation * Math.PI) / 180);
  ctx.globalAlpha = label.alpha ?? 1;
  ctx.font = monoFont(label.size);
  ctx.fillStyle = BRAND.edge;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const lines = labelLines(label);
  const step = label.size * 1.15;
  const top = -(step * (lines.length - 1)) / 2;
  lines.forEach((line, i) => {
    ctx.fillText(line, 0, top + i * step, label.maxWidth);
  });
  ctx.restore();
}

/**
 * Draw the playfield. `width` and `height` are the canvas size in pixels. The height should be
 * twice the width. The function scales to table units.
 */
export function drawPlayfieldArt(ctx: Ctx2DLike, width: number, height: number): void {
  ctx.save();
  ctx.scale(width / PLAYFIELD_WIDTH, height / PLAYFIELD_HEIGHT);
  ctx.globalAlpha = 1;
  ctx.fillStyle = BRAND.field;
  ctx.fillRect(0, 0, PLAYFIELD_WIDTH, PLAYFIELD_HEIGHT);

  // A neutral grid, one line each 100 units.
  ctx.strokeStyle = BRAND.gray222;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = 100; x < PLAYFIELD_WIDTH; x += 100) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, PLAYFIELD_HEIGHT);
  }
  for (let y = 100; y < PLAYFIELD_HEIGHT; y += 100) {
    ctx.moveTo(0, y);
    ctx.lineTo(PLAYFIELD_WIDTH, y);
  }
  ctx.stroke();

  // Outlined squares behind the four lane letters (white edge, no fill).
  ctx.strokeStyle = BRAND.edge;
  ctx.lineWidth = 4;
  for (const { x, box } of LANE_SQUARES) ctx.strokeRect(x - box / 2, 1650 - box / 2, box, box);

  // Outlines of the shot areas: the saucer and the spinner lane.
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(420, 900, 40, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeRect(490, 690, 60, 130);

  for (const a of PLAYFIELD_ARROWS) drawArrow(ctx, a.x, a.y, a.angle, a.size);
  for (const label of PLAYFIELD_LABELS) drawLabel(ctx, label);
  ctx.restore();
}
